import { LegalLayout } from '../components/LegalLayout'

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="2026-10-12">
      <p>
        This Policy explains what data Prism Factory (the "Service") collects, why, and how it is used.
      </p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li>
          <strong>Account data:</strong> email address and authentication identifiers from our identity
          provider (Clerk).
        </li>
        <li>
          <strong>Connected platforms:</strong> when you authorize the Service to access YouTube or
          TikTok on your behalf, we store the OAuth tokens and the public profile information returned
          by those platforms (channel/profile name, ID, avatar).
        </li>
        <li>
          <strong>Instagram:</strong> when you connect an Instagram professional account through
          Instagram Login, we store its account ID, username, account type, the permissions you
          granted, and an access token, which is encrypted at rest. We do not read direct messages,
          followers, or any account other than the ones you connect.
        </li>
        <li>
          <strong>Content you create:</strong> scripts, audio, video files, thumbnails, metadata, and
          related artifacts produced through the Service.
        </li>
        <li>
          <strong>Usage and performance data:</strong> view counts, retention, likes, comments, and
          similar metrics retrieved from connected platforms to display analytics inside the Service.
        </li>
        <li>
          <strong>Logs:</strong> standard server logs (timestamps, request paths, error messages) used
          for debugging and security.
        </li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>To operate the Service: generate, render, schedule, and publish your content.</li>
        <li>
          To call third-party APIs on your behalf (YouTube, TikTok, Google Gemini or Anthropic
          Claude as the AI provider, voice/TTS providers, stock-media providers).
        </li>
        <li>
          To publish Reels to the Instagram accounts you connect, and to read back what was published
          so the Service can confirm each post. It reads the caption and link of the new post and,
          when a publication is uncertain, the caption, link and date of the most recent posts on
          that profile. The Service also asks for permission to read the metrics (insights) of the
          connected account, to show its performance in a future version; it does not read them
          today.
        </li>
        <li>To show you analytics and rankings derived from your own content's performance.</li>
        <li>To maintain security, prevent abuse, and comply with legal obligations.</li>
      </ul>

      <h2>3. Third Parties</h2>
      <p>
        To deliver its features, the Service shares data with the following processors strictly as
        needed:
      </p>
      <ul>
        <li>
          <strong>Clerk</strong> — authentication and user identity.
        </li>
        <li>
          <strong>YouTube (Google)</strong> — when you connect a YouTube channel and publish or fetch
          analytics.
        </li>
        <li>
          <strong>TikTok</strong> — when you connect a TikTok profile and publish or fetch metrics.
        </li>
        <li>
          <strong>Instagram (Meta)</strong> — when you connect an Instagram account, publish Reels
          and the Service reads them back.
        </li>
        <li>
          <strong>Cloudflare (R2)</strong> — temporary storage of the video and cover files that
          Instagram downloads to create a Reel. The files are deleted as soon as Instagram has
          fetched them, and automatically within one day in any case.
        </li>
        <li>
          <strong>Discord</strong> — operational alerts sent to the owner of the Service, or to a
          Discord webhook you configure for your channel, which may include links to published posts
          and error messages.
        </li>
        <li>
          <strong>Google (Gemini API)</strong> — generation of scripts, metadata, and other
          AI-assisted artifacts, when Gemini is the configured AI provider.
        </li>
        <li>
          <strong>Anthropic (Claude API)</strong> — the same, when Claude is the configured AI
          provider.
        </li>
        <li>
          <strong>TTS / stock-media providers</strong> — voice synthesis and stock footage used in
          rendered videos.
        </li>
      </ul>
      <p>Each provider operates under its own privacy policy. We do not sell personal data.</p>

      <h2>4. YouTube, TikTok and Instagram Data</h2>
      <p>
        The Service uses the YouTube API Services, the TikTok APIs and the Instagram API. Your use of
        those features is also subject to the{' '}
        <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
          YouTube Terms of Service
        </a>
        , the{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          Google Privacy Policy
        </a>
        , the{' '}
        <a href="https://www.tiktok.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
          TikTok Privacy Policy
        </a>{' '}
        and the{' '}
        <a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noopener noreferrer">
          Instagram Privacy Policy
        </a>
        . You can revoke our access at any time via your Google account settings, your TikTok
        account settings, or Instagram's Settings → Apps and websites. Disconnecting an Instagram
        account on the channel page deletes its stored token right away.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain account data and content for as long as your account is active. You can request
        deletion of your account and associated content at any time using the contact below; we will
        delete it within 30 days, except where retention is required by law.
      </p>
      <p>
        <strong>Deleting Instagram data:</strong> disconnect the account on the channel page to delete
        its stored token, or email us to delete everything the Service holds about that account.
      </p>

      <h2>6. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have rights to access, correct, export, or delete
        personal data we hold about you. Contact us to exercise those rights.
      </p>

      <h2>7. Security</h2>
      <p>
        We use industry-standard measures (encryption in transit, access controls) to protect data. No
        system is perfectly secure; we cannot guarantee absolute security.
      </p>

      <h2>8. Children</h2>
      <p>
        The Service is not directed to children under 13 (or the minimum age required in your
        jurisdiction). We do not knowingly collect data from children.
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update this Policy. Material changes will be reflected by updating the "Last updated"
        date above.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions or requests:{' '}
        <a href="mailto:prism.video.factory@gmail.com">prism.video.factory@gmail.com</a>.
      </p>
    </LegalLayout>
  )
}
